// Compatibility layer for next/navigation
// Provides useRouter, usePathname, useSearchParams for React Router

import { useNavigate, useLocation, useSearchParams as useRouterSearchParams } from 'react-router-dom';
import { useMemo, useCallback } from 'react';

/**
 * Compatibility hook to replace Next.js useRouter
 * Provides push, replace, back, forward, refresh methods
 * Returns a stable reference to prevent infinite re-renders
 */
export function useRouter() {
  const navigate = useNavigate();
  const location = useLocation();

  // Memoize navigation functions to ensure stable references
  const push = useCallback((url, options) => {
    navigate(url, { replace: false, ...options });
  }, [navigate]);

  const replace = useCallback((url, options) => {
    navigate(url, { replace: true, ...options });
  }, [navigate]);

  const back = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const forward = useCallback(() => {
    navigate(1);
  }, [navigate]);

  const refresh = useCallback(() => {
    navigate(0);
  }, [navigate]);

  const prefetch = useCallback(() => {
    // No-op in Vite/React Router - prefetching is handled differently
  }, []);

  // Memoize the query object
  const query = useMemo(() => 
    Object.fromEntries(new URLSearchParams(location.search)),
    [location.search]
  );

  // Return a memoized router object to prevent infinite re-renders
  // when used in useEffect dependency arrays
  return useMemo(() => ({
    push,
    replace,
    back,
    forward,
    refresh,
    prefetch,
    pathname: location.pathname,
    query,
  }), [push, replace, back, forward, refresh, prefetch, location.pathname, query]);
}

/**
 * Compatibility hook to replace Next.js usePathname
 * Returns the current pathname
 */
export function usePathname() {
  const location = useLocation();
  return location.pathname;
}

/**
 * Compatibility hook to replace Next.js useSearchParams
 * Returns URLSearchParams-like object with get method
 */
export function useSearchParams() {
  const [searchParams] = useRouterSearchParams();
  return searchParams;
}

export default { useRouter, usePathname, useSearchParams };
